import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ShoppingCart } from 'lucide-react';
import { useCart } from '../context/CartContext';

const CoffeeTablesGallery = () => {
  const [coffeeTablesData, setCoffeeTablesData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedProduct, setExpandedProduct] = useState(null);
  const { addToCart } = useCart();

  const apiUrl = process.env.REACT_APP_BACKEND_URL || '';

  // Fetch products from API
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await fetch(`${apiUrl}/api/products/coffee-tables`);
        if (response.ok) {
          const data = await response.json();
          setCoffeeTablesData(data);
        }
      } catch (error) {
        console.error('Failed to fetch coffee tables:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, [apiUrl]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white pt-24 pb-16 flex items-center justify-center">
        <div className="text-[hsl(45,100%,70%)] text-2xl">Loading coffee tables...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white pt-24 pb-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Back Button */}
        <div className="mb-8">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-gray-600 hover:text-[hsl(45,100%,70%)] transition-colors group ml-52"
          >
            <ArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform" />
            Back to home
          </Link>
        </div>

        {/* Header Section */}
        <div className="text-center mb-16">
          <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-gray-900 mb-6">
            Resin <span className="text-[hsl(45,100%,70%)]">Coffee Tables</span>
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Explore our exquisite collection of Italian designer resin tables. Each piece combines 
            artistic craftsmanship with functional elegance, featuring intricate patterns and 
            luxurious materials.
          </p>
          <p className="text-gray-500 text-sm mt-4">
            {coffeeTablesData.length} {coffeeTablesData.length === 1 ? 'piece' : 'pieces'} available
          </p>
        </div>

        {/* Products Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {coffeeTablesData.map((product) => (
            <div
              key={product.id}
              className="group bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 cursor-pointer"
              onClick={() => setExpandedProduct(product)}
            >
              {/* Product Image */}
              <div className="relative aspect-square overflow-hidden bg-gray-100">
                <img
                  src={product.images[0]}
                  alt={product.name}
                  className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-700"
                />
              </div>

              {/* Product Info */}
              <div className="p-6">
                <h3 className="text-xl font-bold text-gray-900 group-hover:text-[hsl(45,100%,70%)] transition-colors">
                  {product.name}
                </h3>
              </div>
            </div>
          ))}
        </div>

        {/* Info Section */}
        <div className="mt-20 bg-gradient-to-r from-[hsl(45,100%,70%)]/10 to-transparent rounded-2xl p-8 md:p-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">
            Italian Craftsmanship
          </h2>
          <p className="text-gray-700 text-lg leading-relaxed max-w-4xl">
            Our resin coffee tables are meticulously handcrafted by skilled Italian artisans. 
            Each table features premium epoxy resin infused with intricate patterns, metallic 
            accents, and translucent colors. From geometric designs to Islamic-inspired motifs, 
            every piece is a unique work of art that transforms your living space into a gallery 
            of contemporary luxury.
          </p>
        </div>
      </div>

      {/* Expanded Product Modal */}
      {expandedProduct && (
        <div
          className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
          onClick={() => setExpandedProduct(null)}
        >
          <div
            className="bg-white rounded-2xl max-w-6xl w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-0">
              {/* Left: Image Gallery */}
              <div className="relative bg-gray-100 min-h-[400px] lg:min-h-[600px] p-8">
                <img
                  src={expandedProduct.images[0]}
                  alt={expandedProduct.name}
                  className="w-full h-full object-contain"
                />
                
                {/* Close Button */}
                <button
                  onClick={() => setExpandedProduct(null)}
                  className="absolute top-4 right-4 w-10 h-10 bg-white rounded-full shadow-lg flex items-center justify-center hover:bg-gray-100"
                >
                  ✕
                </button>
              </div>

              {/* Right: Product Details */}
              <div className="p-8 lg:p-12">
                <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
                  {expandedProduct.name}
                </h2>

                <p className="text-gray-600 text-lg leading-relaxed mb-8">
                  {expandedProduct.description}
                </p>

                {/* Dimensions */}
                <div className="mb-8">
                  <h3 className="text-sm font-bold text-gray-500 uppercase tracking-wider mb-4">
                    Dimensions
                  </h3>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="text-center p-4 bg-gray-50 rounded-lg">
                      <div className="text-2xl font-bold text-[hsl(45,100%,70%)]">
                        {expandedProduct.dimensions.height}
                      </div>
                      <div className="text-xs text-gray-500 mt-1">Height</div>
                    </div>
                    <div className="text-center p-4 bg-gray-50 rounded-lg">
                      <div className="text-2xl font-bold text-[hsl(45,100%,70%)]">
                        {expandedProduct.dimensions.width}
                      </div>
                      <div className="text-xs text-gray-500 mt-1">Width</div>
                    </div>
                    <div className="text-center p-4 bg-gray-50 rounded-lg">
                      <div className="text-2xl font-bold text-[hsl(45,100%,70%)]">
                        {expandedProduct.dimensions.depth}
                      </div>
                      <div className="text-xs text-gray-500 mt-1">Depth</div>
                    </div>
                  </div>
                </div>

                {/* Material */}
                <div className="mb-8">
                  <h3 className="text-sm font-bold text-gray-500 uppercase tracking-wider mb-2">
                    Material
                  </h3>
                  <p className="text-gray-700">{expandedProduct.material}</p>
                </div>

                {/* Price */}
                <div className="mb-8">
                  <div className="text-4xl font-bold text-[hsl(45,100%,70%)]">
                    {expandedProduct.price}
                  </div>
                </div>

                {/* Add to Cart Button */}
                <button 
                  onClick={() => {
                    addToCart({
                      id: expandedProduct.id,
                      name: expandedProduct.name,
                      price: expandedProduct.price,
                      images: expandedProduct.images,
                      category: expandedProduct.category
                    });
                    setExpandedProduct(null);
                  }}
                  className="w-full bg-[hsl(45,100%,70%)] hover:bg-[hsl(45,100%,60%)] text-black font-bold py-4 rounded-lg transition-colors duration-300 flex items-center justify-center gap-2"
                >
                  <ShoppingCart className="w-5 h-5" />
                  Add to Cart
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CoffeeTablesGallery;
